import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { store } from './store';
import App from './App';
import './index.css';
import './lib/brandFonts';
import { applyTheme, readStoredDark } from './lib/theme';
// Polotno removed — card studio uses Konva (see CardEditorPage).

// Apply saved theme before first paint so login/public pages match editorial dark mode.
applyTheme(readStoredDark());

// ── Sentry ─────────────────────────────────────────────────────────────────
// Initialise before rendering so Sentry wraps the React error boundary.
// VITE_SENTRY_DSN is optional — disabled in development when not set.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn:              SENTRY_DSN,
    environment:      import.meta.env.MODE,
    release:          import.meta.env.VITE_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy-safe: mask all text and block all media
        maskAllText:   true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate:   import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.01 : 0,
    replaysOnErrorSampleRate: 1.0,
    // Don't send source of errors back through the network — use source maps
    attachStacktrace: true,
    // Ignore common browser noise
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });
}

// ── Stale chunk recovery ────────────────────────────────────────────────────
// After a new deploy, Vite's content-hashed chunks are replaced. Tabs still
// holding the old index.html fail dynamic import() with vite:preloadError.
// Auto-reload once so the browser picks up the new build; sessionStorage
// prevents an infinite loop if the deploy itself is broken.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  const key = 'chunk-reload-attempted';
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    window.location.reload();
  }
});

// ── Render ──────────────────────────────────────────────────────────────────
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
          <h2>Something went wrong</h2>
          <pre style={{ color: 'red' }}>{String(error)}</pre>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      )}
    >
      <Provider store={store}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
